import { FormEvent, useState } from 'react';
import { ProyectoMock } from '../../../../mocks/proyectos';
import { RespuestaAutor, InformeAvance, RetroalimentacionComite } from './types';
import { calcPorcentajeAvance, getRondaLabel } from './helpers';

interface Props {
  proyectos: ProyectoMock[];
  respuestasEnviadas: RespuestaAutor[];
  respuestasPorCodigo: Record<string, RespuestaAutor[]>;
  informesAvance: InformeAvance[];
  retroalimentaciones: RetroalimentacionComite[];
  retroPorCodigo: Record<string, RetroalimentacionComite[]>;
  onInformeEnviado: (inf: InformeAvance) => void;
  onNuevaRetro: (retro: RetroalimentacionComite) => void;
  onNuevaRespuesta: (resp: RespuestaAutor) => void;
  lastMailRef: React.MutableRefObject<{ subject: string; body: string; email: string } | null>;
}

type SubView = 'selector' | 'detalle' | 'retro-form' | 'simular-doc';

function buildRondaLetter(
  proyecto: ProyectoMock,
  ronda: 2 | 3,
  observaciones: string,
  requiereCorreccion: boolean
): string {
  const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  const rondaLabel = ronda === 2 ? '2ª' : '3ª';

  const lines: string[] = [
    today,
    '',
    `Estimado/a ${proyecto.investigador},`,
    '',
    `Por medio de la presente, el Comité de Investigación e Interacción se dirige a usted en relación con el proyecto titulado "${proyecto.titulo}" (código: ${proyecto.codigo}), a fin de comunicarle la retroalimentación correspondiente a la ${rondaLabel} ronda de revisión.`,
    '',
    `═══════════════════════════════════════════════════════════`,
    `  ${rondaLabel.toUpperCase()} RONDA DE REVISIÓN — Retroalimentación del Comité`,
    `═══════════════════════════════════════════════════════════`,
    '',
    observaciones || '[Ingrese aquí las observaciones del comité sobre el documento corregido enviado por el investigador.]',
    '',
    `─── RESOLUCIÓN DEL COMITÉ CIENTÍFICO ───`,
    '',
    `Ronda: ${rondaLabel} revisión`,
    '',
  ];

  if (requiereCorreccion) {
    lines.push(
      `En virtud de lo expuesto, el Comité de Investigación ha resuelto que el documento corregido requiere ajustes adicionales. Se solicita atender las observaciones indicadas y reenviar el documento corregido dentro del plazo estipulado.`
    );
    if (ronda === 2) {
      lines.push('');
      lines.push('Esta comunicación corresponde a la 2ª ronda de revisión. De ser necesario, el Comité podrá emitir una 3ª y última ronda de retroalimentación.');
    } else {
      lines.push('');
      lines.push('Esta comunicación corresponde a la 3ª y última ronda de revisión. El Comité emitirá la resolución definitiva una vez recibido el documento final.');
    }
  } else {
    lines.push(
      `En virtud de lo expuesto, el Comité de Investigación ha resuelto que el documento corregido cumple satisfactoriamente con los requisitos solicitados. El proyecto queda APROBADO en esta instancia.`
    );
  }

  lines.push('');
  lines.push('─── RECOMENDACIONES ADICIONALES ───');
  lines.push('');
  lines.push('[El Administrador puede incorporar aquí recomendaciones adicionales antes de enviar esta comunicación al autor.]');
  lines.push('');
  lines.push('Sin más que agregar, el Comité de Investigación agradece su participación y queda a su disposición para cualquier consulta.');
  lines.push('');
  lines.push('Atentamente,');
  lines.push('');
  lines.push('Comité de Investigación e Interacción');
  lines.push('Programa de Investigación');

  return lines.join('\n');
}

export default function TabInformeAvance({
  proyectos,
  respuestasEnviadas,
  respuestasPorCodigo,
  informesAvance,
  retroalimentaciones,
  retroPorCodigo,
  onInformeEnviado,
  onNuevaRetro,
  onNuevaRespuesta,
  lastMailRef,
}: Props) {
  const [selectedCode, setSelectedCode] = useState('');
  const [subView, setSubView] = useState<SubView>('selector');
  const [retroRonda, setRetroRonda] = useState<2 | 3>(2);
  const [charCount, setCharCount] = useState({ obs: 0 });
  const [loadingRetro, setLoadingRetro] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [retroRequiereCorreccion, setRetroRequiereCorreccion] = useState<boolean | null>(null);
  const [retroObservaciones, setRetroObservaciones] = useState('');
  const [cartaRetro, setCartaRetro] = useState('');
  // Para simular que el investigador envió el documento (en demo sin backend)
  const [simDocNombre, setSimDocNombre] = useState('');
  const [simDocFecha, setSimDocFecha] = useState(new Date().toISOString().split('T')[0]);
  const [simDocNota, setSimDocNota] = useState('');
  // Vista expandida de documento
  const [docExpandido, setDocExpandido] = useState<string | null>(null);

  const proyectoSel = proyectos.find(p => p.codigo === selectedCode);
  const avanceCalc = proyectoSel ? calcPorcentajeAvance(proyectoSel, respuestasEnviadas, informesAvance, retroalimentaciones) : null;
  const informesSel = informesAvance.filter(i => i.codigo === selectedCode);
  const retrosSel = (retroPorCodigo[selectedCode] || []).sort((a, b) => a.ronda - b.ronda);

  const rondasRetroExistentes = retrosSel.map(r => r.ronda);
  const proximaRondaRetro: 2 | 3 | null =
    !rondasRetroExistentes.includes(2) ? 2 :
    !rondasRetroExistentes.includes(3) ? 3 :
    null;

  const respuestasProy = (respuestasPorCodigo[selectedCode] || []).sort((a, b) => a.ronda - b.ronda);

  const handleSelectProject = (codigo: string) => {
    setSelectedCode(codigo);
    setSubView(codigo ? 'detalle' : 'selector');
    setSuccessMsg('');
  };

  const handleObsChange = (val: string) => {
    setRetroObservaciones(val);
    setCharCount({ obs: val.length });
    if (proyectoSel) {
      setCartaRetro(buildRondaLetter(proyectoSel, retroRonda, val, retroRequiereCorreccion ?? true));
    }
  };

  const handleRequiereCorreccionChange = (val: boolean) => {
    setRetroRequiereCorreccion(val);
    if (proyectoSel) {
      setCartaRetro(buildRondaLetter(proyectoSel, retroRonda, retroObservaciones, val));
    }
  };

  const handleOpenRetroForm = (ronda: 2 | 3) => {
    setRetroRonda(ronda);
    setRetroObservaciones('');
    setRetroRequiereCorreccion(null);
    if (proyectoSel) {
      setCartaRetro(buildRondaLetter(proyectoSel, ronda, '', true));
    }
    setSubView('retro-form');
  };

  // Simular que el investigador envió el documento (demo)
  const handleSimularDoc = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!proyectoSel || !avanceCalc) return;
    setLoadingDoc(true);
    try {
      const nuevoInforme: InformeAvance = {
        id: `inf-${Date.now()}`,
        codigo: proyectoSel.codigo,
        titulo: proyectoSel.titulo,
        investigador: proyectoSel.investigador,
        porcentaje: avanceCalc.porcentaje,
        porcentajeDetalle: avanceCalc.detalle,
        fechaInforme: simDocFecha,
        actividadesRealizadas: simDocNota,
        proximasActividades: '',
        observacionesAvance: '',
        fechaEnvio: new Date().toISOString().split('T')[0],
      };
      onInformeEnviado(nuevoInforme);
      setSuccessMsg('Documento corregido del investigador registrado. Ahora puede revisarlo y emitir la siguiente ronda.');
      setSubView('detalle');
      setSimDocNombre('');
      setSimDocNota('');
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleSubmitRetro = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!proyectoSel || retroRequiereCorreccion === null) return;
    setLoadingRetro(true);
    try {
      const nuevaRetro: RetroalimentacionComite = {
        id: `retro-${Date.now()}`,
        codigo: selectedCode,
        ronda: retroRonda,
        fechaRetro: new Date().toISOString().split('T')[0],
        observaciones: retroObservaciones,
        requiereNuevaCorreccion: retroRequiereCorreccion,
        estadoFinal: retroRequiereCorreccion ? 'correccion' : 'aprobado',
      };
      onNuevaRetro(nuevaRetro);

      const subject = encodeURIComponent(
        `${retroRonda === 2 ? '2ª' : '3ª'} Ronda de Revisión — Proyecto ${proyectoSel.codigo}`
      );
      const body = encodeURIComponent(cartaRetro);
      window.open(`mailto:${proyectoSel.email}?subject=${subject}&body=${body}`, '_blank');

      setSuccessMsg(`${getRondaLabel(retroRonda)} de retroalimentación registrada y carta enviada al autor.`);
      setSubView('detalle');
    } finally {
      setLoadingRetro(false);
    }
  };

  return (
    <div>
      {/* Banner informativo */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-5 flex items-start gap-3">
        <span className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-md flex-shrink-0">
          <i className="ri-shield-user-line text-teal-700" />
        </span>
        <div>
          <p className="text-sm font-bold text-teal-800 mb-0.5">Seguimiento de avance — Comité de Investigación</p>
          <p className="text-xs text-teal-700 leading-relaxed">
            La <strong>1ª ronda</strong> de solicitud de revisión se emite desde &quot;Resultados y Decisión&quot;.
            Aquí el investigador envía su documento corregido y el comité, tras revisarlo, emite la <strong>2ª y 3ª ronda</strong> de retroalimentación con carta oficial.
          </p>
        </div>
      </div>

      {/* Leyenda de colores */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-5">
        <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-palette-line" /></span>
          Leyenda de estados
        </p>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-amber-400 flex-shrink-0" />
            <span className="text-xs text-slate-600"><strong className="text-amber-700">Ámbar/Naranja</strong> — Pendiente: aún no se ha recibido el documento corregido del investigador</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500 flex-shrink-0" />
            <span className="text-xs text-slate-600"><strong className="text-emerald-700">Verde</strong> — Recibido: el documento corregido ya fue enviado por el investigador y está listo para revisar</span>
          </div>
        </div>
      </div>

      {/* Selector de proyecto */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-semibold text-teal-700 mb-3 flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-folder-info-line" /></span>
          Selección del Proyecto
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Código del proyecto</label>
            <select
              value={selectedCode}
              onChange={e => handleSelectProject(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 cursor-pointer"
            >
              <option value="">Seleccionar proyecto...</option>
              {proyectos.map(p => {
                const informes = informesAvance.filter(i => i.codigo === p.codigo).length;
                const retros = (retroPorCodigo[p.codigo] || []).length;
                return (
                  <option key={p.codigo} value={p.codigo}>
                    {p.codigo} – {p.titulo.substring(0, 38)}{p.titulo.length > 38 ? '...' : ''}
                    {informes > 0 ? ` · ${informes} doc.` : ''}
                    {retros > 0 ? ` · ${retros} retro` : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Investigador <span className="text-xs font-normal text-slate-400">(automático)</span>
            </label>
            <div className="w-full border border-slate-100 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-600 flex items-center gap-2 min-h-[38px]">
              {proyectoSel ? (
                <>
                  <span className="w-4 h-4 flex items-center justify-center text-teal-500 flex-shrink-0"><i className="ri-user-line" /></span>
                  <span className="font-medium">{proyectoSel.investigador}</span>
                </>
              ) : (
                <span className="text-slate-400 italic text-xs">Seleccione un proyecto</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Estado vacío */}
      {!proyectoSel && (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-12 text-center text-slate-400">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <i className="ri-folder-open-line text-4xl" />
          </div>
          <p className="text-sm font-medium">Seleccione un proyecto para ver su seguimiento</p>
          <p className="text-xs mt-1">Elija un proyecto del selector de arriba para gestionar el avance y las rondas de revisión.</p>
        </div>
      )}

      {/* Mensaje de éxito */}
      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-3">
          <span className="w-5 h-5 flex items-center justify-center text-emerald-600 flex-shrink-0"><i className="ri-checkbox-circle-fill" /></span>
          <p className="text-sm font-semibold text-emerald-700">{successMsg}</p>
          <button onClick={() => setSuccessMsg('')} className="ml-auto cursor-pointer text-emerald-500 hover:text-emerald-700">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-close-line" /></span>
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          VISTA DETALLE DEL PROYECTO
      ══════════════════════════════════════════════════════════ */}
      {proyectoSel && subView === 'detalle' && (
        <div className="space-y-5">

          {/* Barra de avance */}
          {avanceCalc && (
            <div className="bg-white border border-teal-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-teal-700 mb-3 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-percent-line" /></span>
                Porcentaje de avance — calculado automáticamente
              </h3>
              <div className="mb-3">
                <div className="flex items-end justify-between mb-2">
                  <span className="text-xs text-slate-500">Avance del proyecto</span>
                  <span className={`text-3xl font-black ${avanceCalc.porcentaje >= 80 ? 'text-emerald-600' : avanceCalc.porcentaje >= 50 ? 'text-teal-600' : 'text-amber-500'}`}>
                    {avanceCalc.porcentaje}%
                  </span>
                </div>
                <div className="bg-slate-100 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all duration-700 ${avanceCalc.porcentaje >= 80 ? 'bg-emerald-500' : avanceCalc.porcentaje >= 50 ? 'bg-teal-500' : 'bg-amber-400'}`}
                    style={{ width: `${avanceCalc.porcentaje}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ══ SECCIÓN CENTRAL: Documento corregido del investigador ══ */}
          <div className={`rounded-xl border-2 overflow-hidden ${informesSel.length > 0 ? 'border-emerald-300' : 'border-amber-300'}`}>

            {/* Cabecera de estado */}
            <div className={`px-5 py-4 flex items-center gap-4 ${informesSel.length > 0 ? 'bg-emerald-600' : 'bg-amber-500'}`}>
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/20 flex-shrink-0">
                <i className={`text-white text-xl ${informesSel.length > 0 ? 'ri-file-check-line' : 'ri-file-upload-line'}`} />
              </div>
              <div className="flex-1">
                <p className="text-base font-bold text-white">
                  {informesSel.length > 0
                    ? `Documento corregido recibido del investigador`
                    : 'Esperando documento corregido del investigador'}
                </p>
                <p className="text-xs text-white/80 mt-0.5">
                  {informesSel.length > 0
                    ? `${informesSel.length} documento${informesSel.length > 1 ? 's' : ''} recibido${informesSel.length > 1 ? 's' : ''} — el comité puede revisarlo y emitir la siguiente ronda`
                    : 'El investigador debe enviar el documento corregido en respuesta a la 1ª ronda de revisión emitida'}
                </p>
              </div>
              {informesSel.length > 0 && (
                <span className="flex-shrink-0 bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full border border-white/30">
                  {informesSel.length} doc.
                </span>
              )}
            </div>

            {/* Cuerpo */}
            <div className={`p-5 ${informesSel.length > 0 ? 'bg-emerald-50' : 'bg-amber-50'}`}>

              {/* Sin documento: instrucción clara */}
              {informesSel.length === 0 && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg border border-amber-200 p-4">
                    <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line" /></span>
                      ¿Cómo funciona este paso?
                    </p>
                    <ol className="space-y-2 text-xs text-amber-700 list-none">
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 flex items-center justify-center bg-amber-200 rounded-full text-amber-800 font-bold flex-shrink-0 text-[10px]">1</span>
                        El investigador recibe la carta de la <strong>1ª ronda</strong> (emitida desde &quot;Resultados y Decisión&quot;) y envía su documento corregido al comité.
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 flex items-center justify-center bg-amber-200 rounded-full text-amber-800 font-bold flex-shrink-0 text-[10px]">2</span>
                        El comité recibe el documento, lo revisa y lo registra aquí para habilitar la <strong>2ª ronda de retroalimentación</strong>.
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 flex items-center justify-center bg-amber-200 rounded-full text-amber-800 font-bold flex-shrink-0 text-[10px]">3</span>
                        Tras registrar el documento, el comité puede generar y enviar la <strong>carta de 2ª ronda</strong> automáticamente.
                      </li>
                    </ol>
                  </div>

                  <button
                    onClick={() => setSubView('simular-doc')}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-3 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 px-5 py-4 rounded-xl transition-colors cursor-pointer"
                  >
                    <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-md">
                      <i className="ri-file-add-line" />
                    </span>
                    Registrar documento corregido recibido del investigador
                  </button>
                </div>
              )}

              {/* Con documentos: lista de documentos recibidos */}
              {informesSel.length > 0 && (
                <div className="space-y-3">
                  {informesSel.map((inf, idx) => (
                    <DocumentoRecibido
                      key={inf.id}
                      informe={inf}
                      index={idx}
                      expanded={docExpandido === inf.id}
                      onToggle={() => setDocExpandido(docExpandido === inf.id ? null : inf.id)}
                    />
                  ))}

                  {/* Acción siguiente */}
                  {proximaRondaRetro !== null && (
                    <div className="mt-4 pt-4 border-t border-emerald-200">
                      <p className="text-xs text-emerald-700 font-medium mb-3 flex items-center gap-2">
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-circle-line" /></span>
                        Documento revisado — ahora puede emitir la <strong className="ml-1">{proximaRondaRetro === 2 ? '2ª' : '3ª'} ronda de retroalimentación</strong>
                      </p>
                      <button
                        onClick={() => handleOpenRetroForm(proximaRondaRetro)}
                        className="whitespace-nowrap w-full flex items-center justify-center gap-3 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 px-5 py-4 rounded-xl transition-colors cursor-pointer"
                      >
                        <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-md">
                          <i className="ri-send-plane-fill" />
                        </span>
                        Generar y enviar carta de {proximaRondaRetro === 2 ? '2ª' : '3ª'} ronda al investigador
                      </button>
                    </div>
                  )}

                  {proximaRondaRetro === null && (
                    <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-white border border-emerald-200 px-4 py-3 rounded-lg">
                      <span className="w-5 h-5 flex items-center justify-center"><i className="ri-checkbox-circle-fill" /></span>
                      Proceso de retroalimentación completado (máximo 3 rondas)
                    </div>
                  )}

                  <button
                    onClick={() => setSubView('simular-doc')}
                    className="whitespace-nowrap flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-white border border-emerald-200 px-3 py-2 rounded-md hover:bg-emerald-50 transition-colors cursor-pointer"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line" /></span>
                    Registrar otro documento recibido
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── HISTORIAL DE RONDAS ── */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-teal-700 mb-4 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-git-branch-line" /></span>
              Historial de rondas de revisión
            </h3>

            {/* Ronda 1 */}
            <RondaItem
              numero={1}
              titulo="1ª Ronda — Decisión oficial emitida"
              subtitulo={proyectoSel.decision && proyectoSel.decision !== '—'
                ? `Decisión: ${proyectoSel.decision} · Carta enviada al investigador desde "Resultados y Decisión"`
                : 'Sin decisión emitida aún — debe emitirse desde la sección "Resultados y Decisión"'}
              fecha={proyectoSel.fechaDecision && proyectoSel.fechaDecision !== '—' ? proyectoSel.fechaDecision : undefined}
              estado={proyectoSel.decision && proyectoSel.decision !== '—' ? 'completado' : 'pendiente'}
              icono="ri-award-line"
              badge={proyectoSel.decision && proyectoSel.decision !== '—' ? '1ª Ronda enviada' : undefined}
              badgeColor="teal"
            />

            {/* Documento del investigador */}
            <RondaDocInvestigador
              informes={informesSel}
              tieneDoc={informesSel.length > 0}
              onRegistrar={() => setSubView('simular-doc')}
            />

            {/* 2ª Retroalimentación */}
            {(informesSel.length > 0 || retrosSel.find(r => r.ronda === 2)) && (
              <>
                <RondaRetroComite
                  ronda={2}
                  retro={retrosSel.find(r => r.ronda === 2)}
                  onEmitir={() => handleOpenRetroForm(2)}
                  puedeEmitir={informesSel.length > 0 && !retrosSel.find(r => r.ronda === 2)}
                />

                {/* 2ª Corrección del investigador */}
                {retrosSel.find(r => r.ronda === 2)?.requiereNuevaCorreccion && (
                  <RondaDocInvestigador
                    informes={[]}
                    respuestas={respuestasProy.filter(r => r.ronda === 2)}
                    tieneDoc={respuestasProy.filter(r => r.ronda === 2).length > 0}
                    onRegistrar={() => setSubView('simular-doc')}
                    label="2ª Corrección del investigador"
                    sublabel="El investigador envía el documento corregido en respuesta a la 2ª ronda"
                  />
                )}
              </>
            )}

            {/* 3ª Retroalimentación */}
            {retrosSel.find(r => r.ronda === 2) && (
              retrosSel.find(r => r.ronda === 2)?.requiereNuevaCorreccion
                ? (respuestasProy.filter(r => r.ronda === 2).length > 0 || retrosSel.find(r => r.ronda === 3)) && (
                  <RondaRetroComite
                    ronda={3}
                    retro={retrosSel.find(r => r.ronda === 3)}
                    onEmitir={() => handleOpenRetroForm(3)}
                    puedeEmitir={respuestasProy.filter(r => r.ronda === 2).length > 0 && !retrosSel.find(r => r.ronda === 3)}
                  />
                )
                : (
                  <div className="flex gap-4 mb-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 bg-emerald-500 border-emerald-500 text-white">
                        <i className="ri-checkbox-circle-fill text-sm" />
                      </div>
                    </div>
                    <div className="flex-1 pb-4 rounded-lg border p-3 mb-1 bg-emerald-50 border-emerald-200">
                      <p className="text-sm font-semibold text-emerald-800">Proceso completado — Trabajo aprobado</p>
                      <p className="text-xs text-emerald-700 mt-0.5">El comité aprobó el documento en la 2ª ronda. No se requieren más correcciones.</p>
                    </div>
                  </div>
                )
            )}

            {/* 3ª Corrección del investigador */}
            {retrosSel.find(r => r.ronda === 3)?.requiereNuevaCorreccion && (
              <RondaDocInvestigador
                informes={[]}
                respuestas={respuestasProy.filter(r => r.ronda === 3)}
                tieneDoc={respuestasProy.filter(r => r.ronda === 3).length > 0}
                onRegistrar={() => setSubView('simular-doc')}
                label="3ª Corrección del investigador"
                sublabel="El investigador envía el documento corregido en respuesta a la 3ª ronda"
              />
            )}

            {/* Proceso completado (3 rondas) */}
            {retrosSel.find(r => r.ronda === 3) && !retrosSel.find(r => r.ronda === 3)?.requiereNuevaCorreccion && (
              <div className="flex gap-4 mb-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 bg-emerald-500 border-emerald-500 text-white">
                    <i className="ri-checkbox-circle-fill text-sm" />
                  </div>
                </div>
                <div className="flex-1 pb-4 rounded-lg border p-3 mb-1 bg-emerald-50 border-emerald-200">
                  <p className="text-sm font-semibold text-emerald-800">Proceso completado — Trabajo aprobado en 3ª ronda</p>
                  <p className="text-xs text-emerald-700 mt-0.5">El comité aprobó el documento en la 3ª y última ronda.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          FORMULARIO: Registrar documento recibido del investigador
      ══════════════════════════════════════════════════════════ */}
      {proyectoSel && subView === 'simular-doc' && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => setSubView('detalle')}
              className="whitespace-nowrap flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-700 cursor-pointer transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
              Volver al detalle
            </button>
            <span className="text-slate-300">|</span>
            <span className="text-sm font-semibold text-amber-700">
              Registrar documento recibido — {proyectoSel.codigo}
            </span>
          </div>

          {/* Banner */}
          <div className="bg-amber-500 rounded-xl p-5 flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
              <i className="ri-file-upload-line text-white text-2xl" />
            </div>
            <div>
              <p className="text-base font-bold text-white mb-1">Documento corregido enviado por el investigador</p>
              <p className="text-sm text-white/85 leading-relaxed">
                El investigador envió su documento corregido en respuesta a la solicitud de revisión.
                Registre la recepción para poder revisarlo y emitir la siguiente ronda de retroalimentación.
              </p>
            </div>
          </div>

          {/* Info del proyecto */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
            <span className="w-4 h-4 flex items-center justify-center text-teal-600 flex-shrink-0 mt-0.5"><i className="ri-information-line" /></span>
            <div>
              <p className="text-xs font-semibold text-slate-700">{proyectoSel.titulo}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Código: <span className="font-mono font-bold text-teal-700">{proyectoSel.codigo}</span>
                <span className="mx-2">·</span>
                Investigador/a: <strong>{proyectoSel.investigador}</strong>
                <span className="mx-2">·</span>
                Correo: <span className="text-teal-600">{proyectoSel.email}</span>
              </p>
            </div>
          </div>

          <form onSubmit={handleSimularDoc} className="space-y-5">
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
              <h3 className="text-sm font-semibold text-teal-700 mb-2 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-text-line" /></span>
                Datos del documento recibido
              </h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre del archivo / documento <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                      <i className="ri-attachment-line" />
                    </span>
                    <input
                      type="text"
                      required
                      value={simDocNombre}
                      onChange={e => setSimDocNombre(e.target.value)}
                      placeholder="Ej: Informe_corregido_v2_Apellido.pdf"
                      className="w-full border border-slate-200 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                    />
                  </div>
                  <div className="flex-shrink-0 bg-slate-100 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-500 flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-pdf-line text-red-400" /></span>
                    PDF / DOCX
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Ingrese el nombre del archivo tal como lo envió el investigador.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Fecha de recepción <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={simDocFecha}
                  onChange={e => setSimDocFecha(e.target.value)}
                  className="w-full max-w-xs border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nota interna del comité <span className="text-slate-400 font-normal text-xs">(opcional)</span>
                </label>
                <textarea
                  value={simDocNota}
                  onChange={e => setSimDocNota(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Observaciones internas sobre el documento recibido (no se envían al investigador)..."
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none"
                />
              </div>
            </div>

            {/* Qué pasa después */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start gap-3">
              <span className="w-5 h-5 flex items-center justify-center text-teal-600 flex-shrink-0 mt-0.5"><i className="ri-arrow-right-circle-line" /></span>
              <div>
                <p className="text-xs font-semibold text-teal-800 mb-1">¿Qué pasa después de registrar?</p>
                <p className="text-xs text-teal-700 leading-relaxed">
                  El documento quedará disponible para que el comité lo revise. Una vez revisado, podrá generar automáticamente la <strong>carta de 2ª ronda de retroalimentación</strong> con la misma plantilla de la decisión oficial y enviarla al investigador.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSubView('detalle')}
                className="whitespace-nowrap px-5 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loadingDoc}
                className="whitespace-nowrap px-6 py-2.5 text-sm font-bold text-white bg-amber-500 rounded-md hover:bg-amber-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center gap-2"
              >
                {loadingDoc ? (
                  <><span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line animate-spin" /></span> Registrando...</>
                ) : (
                  <><span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line" /></span> Registrar documento recibido</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          FORMULARIO: 2ª / 3ª Ronda de retroalimentación
      ══════════════════════════════════════════════════════════ */}
      {proyectoSel && subView === 'retro-form' && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => setSubView('detalle')}
              className="whitespace-nowrap flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-700 cursor-pointer transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
              Volver al detalle
            </button>
            <span className="text-slate-300">|</span>
            <span className="text-sm font-semibold text-teal-700">
              {retroRonda === 2 ? '2ª' : '3ª'} Ronda de Retroalimentación — {proyectoSel.codigo}
            </span>
          </div>

          {/* Banner */}
          <div className="bg-teal-700 rounded-xl p-5 flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-xl flex-shrink-0 border border-teal-500">
              <i className="ri-shield-star-line text-white text-2xl" />
            </div>
            <div>
              <p className="text-base font-bold text-white mb-1">
                {retroRonda === 2 ? '2ª' : '3ª'} Ronda de Retroalimentación — Comité de Investigación
              </p>
              <p className="text-sm text-teal-200 leading-relaxed">
                Tras revisar el documento corregido del investigador, complete las observaciones.
                La carta se genera automáticamente con la misma plantilla de la decisión oficial y se enviará por correo al autor.
              </p>
            </div>
          </div>

          {/* Documento que se está evaluando */}
          {informesSel.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-check-line" /></span>
                Documento corregido que está evaluando
              </p>
              <div className="flex items-center gap-3 bg-white rounded-md border border-emerald-100 px-4 py-3">
                <span className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-md flex-shrink-0">
                  <i className="ri-file-pdf-line text-emerald-600" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-800">
                    {informesSel[informesSel.length - 1].actividadesRealizadas || `Documento corregido — ${proyectoSel.codigo}`}
                  </p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Recibido el {informesSel[informesSel.length - 1].fechaInforme} · Investigador: {proyectoSel.investigador}
                  </p>
                </div>
                <span className="flex-shrink-0 bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-md">
                  Recibido
                </span>
              </div>
            </div>
          )}

          {/* Info del proyecto */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
            <span className="w-4 h-4 flex items-center justify-center text-teal-600 flex-shrink-0 mt-0.5"><i className="ri-information-line" /></span>
            <div>
              <p className="text-xs font-semibold text-slate-700">{proyectoSel.titulo}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Código: <span className="font-mono font-bold text-teal-700">{proyectoSel.codigo}</span>
                <span className="mx-2">·</span>
                Investigador/a: <strong>{proyectoSel.investigador}</strong>
                <span className="mx-2">·</span>
                Correo: <span className="text-teal-600">{proyectoSel.email}</span>
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmitRetro} className="space-y-5">

            {/* Paso 1: Resolución */}
            <section className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-teal-700 mb-4 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center bg-teal-600 text-white rounded-full text-xs font-bold">1</span>
                Resolución del Comité
              </h3>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                ¿El trabajo requiere una nueva corrección? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleRequiereCorreccionChange(true)}
                  className={`cursor-pointer flex items-start gap-3 p-4 border-2 rounded-lg transition-all text-left ${retroRequiereCorreccion === true ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white hover:border-amber-200'}`}
                >
                  <span className={`w-5 h-5 flex items-center justify-center rounded-full border-2 flex-shrink-0 mt-0.5 ${retroRequiereCorreccion === true ? 'border-amber-500 bg-amber-500' : 'border-slate-300'}`}>
                    {retroRequiereCorreccion === true && <i className="ri-check-line text-white text-xs" />}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Sí, requiere corrección</p>
                    <p className="text-xs text-amber-600 mt-0.5">El investigador deberá enviar una nueva versión corregida.</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleRequiereCorreccionChange(false)}
                  className={`cursor-pointer flex items-start gap-3 p-4 border-2 rounded-lg transition-all text-left ${retroRequiereCorreccion === false ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:border-emerald-200'}`}
                >
                  <span className={`w-5 h-5 flex items-center justify-center rounded-full border-2 flex-shrink-0 mt-0.5 ${retroRequiereCorreccion === false ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                    {retroRequiereCorreccion === false && <i className="ri-check-line text-white text-xs" />}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">No, trabajo aprobado</p>
                    <p className="text-xs text-emerald-600 mt-0.5">El documento cumple con los requisitos del comité.</p>
                  </div>
                </button>
              </div>
            </section>

            {/* Paso 2: Observaciones */}
            <section className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-teal-700 mb-4 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center bg-teal-600 text-white rounded-full text-xs font-bold">2</span>
                Observaciones del Comité
                <span className="ml-auto text-xs font-normal text-slate-400">({charCount.obs}/500)</span>
              </h3>
              <textarea
                name="observaciones_retro"
                required
                rows={5}
                maxLength={500}
                value={retroObservaciones}
                onChange={e => handleObsChange(e.target.value)}
                placeholder="Detalle las observaciones del comité sobre el documento corregido. Indique qué aspectos fueron atendidos correctamente y cuáles aún requieren ajuste..."
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none"
              />
            </section>

            {/* Paso 3: Carta generada automáticamente */}
            <section className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-teal-700 mb-3 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center bg-teal-600 text-white rounded-full text-xs font-bold">3</span>
                Carta de comunicación al autor
                <span className="ml-2 text-xs font-normal text-slate-400">— generada automáticamente</span>
              </h3>
              <div className="flex items-start gap-2 bg-teal-50 border border-teal-100 rounded-md px-3 py-2 mb-3">
                <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-teal-600 mt-0.5">
                  <i className="ri-file-text-line" />
                </span>
                <p className="text-xs text-teal-700 leading-relaxed">
                  Esta carta usa la misma plantilla de la decisión oficial. Se actualiza automáticamente al completar los campos anteriores. Puede editarla antes de enviar.
                </p>
              </div>
              <textarea
                value={cartaRetro}
                onChange={e => setCartaRetro(e.target.value)}
                rows={14}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none font-mono leading-relaxed"
              />
            </section>

            {retroRequiereCorreccion === null && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-700 flex items-start gap-2">
                <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-information-line" /></span>
                Seleccione si el trabajo requiere corrección o está aprobado para habilitar el envío.
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSubView('detalle')}
                className="whitespace-nowrap px-5 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loadingRetro || retroRequiereCorreccion === null}
                className="whitespace-nowrap px-6 py-2.5 text-sm font-bold text-white bg-teal-700 rounded-md hover:bg-teal-800 transition-colors cursor-pointer disabled:opacity-60 flex items-center gap-2"
              >
                {loadingRetro ? (
                  <><span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line animate-spin" /></span> Enviando...</>
                ) : (
                  <><span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-fill" /></span>
                  Registrar y enviar {retroRonda === 2 ? '2ª' : '3ª'} ronda al autor</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Componente: Documento recibido del investigador ──
interface DocumentoRecibidoProps {
  informe: InformeAvance;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}

function DocumentoRecibido({ informe, index, expanded, onToggle }: DocumentoRecibidoProps) {
  return (
    <div className="bg-white rounded-lg border border-emerald-200 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
          <i className="ri-file-check-line text-emerald-600 text-lg" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-800 truncate">
            {informe.actividadesRealizadas || `Documento corregido #${index + 1}`}
          </p>
          <p className="text-xs text-emerald-600 mt-0.5">
            Recibido el {informe.fechaInforme}
            {informe.porcentaje > 0 && ` · Avance: ${informe.porcentaje}%`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
            Recibido
          </span>
          <button
            onClick={onToggle}
            className="cursor-pointer flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 transition-colors px-2 py-1 rounded-md hover:bg-teal-50"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={expanded ? 'ri-eye-off-line' : 'ri-eye-line'} />
            </span>
            {expanded ? 'Ocultar' : 'Revisar'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-emerald-100 bg-emerald-50/50 space-y-3">
          <div className="bg-white rounded-md border border-emerald-100 p-3">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-file-text-line" /></span>
              Contenido del documento
            </p>
            {informe.actividadesRealizadas ? (
              <p className="text-xs text-slate-700 leading-relaxed">{informe.actividadesRealizadas}</p>
            ) : (
              <p className="text-xs text-slate-400 italic">Sin descripción adicional registrada.</p>
            )}
          </div>
          {informe.porcentaje > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Avance:</span>
              <div className="flex-1 bg-slate-100 rounded-full h-2 max-w-32">
                <div
                  className={`h-2 rounded-full ${informe.porcentaje >= 80 ? 'bg-emerald-500' : informe.porcentaje >= 50 ? 'bg-teal-500' : 'bg-amber-400'}`}
                  style={{ width: `${informe.porcentaje}%` }}
                />
              </div>
              <span className={`text-sm font-bold ${informe.porcentaje >= 80 ? 'text-emerald-600' : informe.porcentaje >= 50 ? 'text-teal-600' : 'text-amber-500'}`}>
                {informe.porcentaje}%
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Componente: Ítem de ronda en el timeline ──
interface RondaItemProps {
  numero: number;
  titulo: string;
  subtitulo: string;
  fecha?: string;
  estado: 'completado' | 'pendiente';
  icono: string;
  badge?: string;
  badgeColor?: 'emerald' | 'amber' | 'teal';
}

function RondaItem({ numero, titulo, subtitulo, fecha, estado, icono, badge, badgeColor = 'teal' }: RondaItemProps) {
  const badgeClasses: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
  };

  return (
    <div className="flex gap-4 mb-4">
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 ${estado === 'completado' ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-slate-200 text-slate-400'}`}>
          {estado === 'completado' ? <i className={`${icono} text-sm`} /> : <span className="text-xs font-bold">{numero}</span>}
        </div>
        <div className="w-0.5 flex-1 bg-slate-200 mt-1 min-h-[20px]" />
      </div>
      <div className={`flex-1 pb-4 rounded-lg border p-3 mb-1 ${estado === 'completado' ? 'bg-white border-teal-100' : 'bg-slate-50 border-dashed border-slate-200'}`}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <p className={`text-sm font-semibold ${estado === 'completado' ? 'text-slate-800' : 'text-slate-400'}`}>{titulo}</p>
            <p className={`text-xs mt-0.5 leading-relaxed ${estado === 'completado' ? 'text-slate-600' : 'text-slate-400 italic'} max-w-lg`}>
              {subtitulo.length > 140 ? subtitulo.substring(0, 140) + '...' : subtitulo}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {fecha && <span className="text-[10px] text-slate-400">{fecha}</span>}
            {badge && (
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badgeClasses[badgeColor]}`}>
                {badge}
              </span>
            )}
            {estado === 'pendiente' && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-400 border-slate-200">
                Pendiente
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Componente: Documento del investigador en el timeline ──
interface RondaDocInvestigadorProps {
  informes?: InformeAvance[];
  respuestas?: RespuestaAutor[];
  tieneDoc: boolean;
  onRegistrar: () => void;
  label?: string;
  sublabel?: string;
}

function RondaDocInvestigador({
  informes = [],
  respuestas = [],
  tieneDoc,
  onRegistrar,
  label = 'Documento corregido del investigador',
  sublabel = 'El investigador envía el documento corregido en respuesta a la 1ª ronda',
}: RondaDocInvestigadorProps) {
  return (
    <div className="flex gap-4 mb-4">
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 ${tieneDoc ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-amber-300 text-amber-500'}`}>
          {tieneDoc ? <i className="ri-check-line text-sm" /> : <i className="ri-time-line text-sm" />}
        </div>
        <div className="w-0.5 flex-1 bg-slate-200 mt-1 min-h-[20px]" />
      </div>
      <div className={`flex-1 pb-4 rounded-lg border p-3 mb-1 ${tieneDoc ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200 border-dashed'}`}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <p className={`text-sm font-semibold ${tieneDoc ? 'text-emerald-800' : 'text-amber-700'}`}>{label}</p>
            <p className={`text-xs mt-0.5 ${tieneDoc ? 'text-emerald-700' : 'text-amber-600 italic'}`}>
              {tieneDoc
                ? `Documento recibido · ${informes[0]?.fechaInforme || respuestas[0]?.fechaEnvio || ''}`
                : sublabel}
            </p>
          </div>
          {!tieneDoc && (
            <button
              onClick={onRegistrar}
              className="whitespace-nowrap cursor-pointer flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors text-amber-700 bg-white border-amber-200 hover:bg-amber-100"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line" /></span>
              Registrar
            </button>
          )}
          {tieneDoc && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
              Recibido
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Componente: Ronda de retroalimentación del comité ──
interface RondaRetroComiteProps {
  ronda: 2 | 3;
  retro?: RetroalimentacionComite;
  onEmitir: () => void;
  puedeEmitir: boolean;
}

function RondaRetroComite({ ronda, retro, onEmitir, puedeEmitir }: RondaRetroComiteProps) {
  const [expanded, setExpanded] = useState(false);
  const rondaLabel = ronda === 2 ? '2ª' : '3ª';

  return (
    <div className="flex gap-4 mb-4">
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 ${retro ? 'bg-teal-600 border-teal-600 text-white' : puedeEmitir ? 'bg-white border-teal-400 text-teal-500' : 'bg-white border-slate-200 text-slate-400'}`}>
          {retro ? <i className="ri-feedback-line text-sm" /> : <span className="text-xs font-bold">{ronda}</span>}
        </div>
        <div className="w-0.5 flex-1 bg-slate-200 mt-1 min-h-[20px]" />
      </div>
      <div className={`flex-1 pb-4 rounded-lg border p-3 mb-1 ${retro ? 'bg-white border-teal-100' : puedeEmitir ? 'bg-teal-50 border-teal-200' : 'bg-slate-50 border-dashed border-slate-200'}`}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1">
            <p className={`text-sm font-semibold ${retro ? 'text-slate-800' : puedeEmitir ? 'text-teal-700' : 'text-slate-400'}`}>
              {rondaLabel} Retroalimentación del Comité
            </p>
            {retro ? (
              <p className="text-xs mt-0.5 text-slate-600 leading-relaxed max-w-lg">
                {retro.observaciones.length > 100 ? retro.observaciones.substring(0, 100) + '...' : retro.observaciones}
              </p>
            ) : (
              <p className={`text-xs mt-0.5 italic ${puedeEmitir ? 'text-teal-600' : 'text-slate-400'}`}>
                {puedeEmitir ? `Listo para emitir la ${rondaLabel} ronda de retroalimentación` : 'Esperando documento corregido del investigador'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {retro && (
              <>
                <span className="text-[10px] text-slate-400">{retro.fechaRetro}</span>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${retro.requiereNuevaCorreccion ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                  {retro.requiereNuevaCorreccion ? 'Requiere corrección' : 'Aprobado'}
                </span>
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="cursor-pointer flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 transition-colors"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className={expanded ? 'ri-eye-off-line' : 'ri-eye-line'} /></span>
                  {expanded ? 'Ocultar' : 'Ver'}
                </button>
              </>
            )}
            {puedeEmitir && !retro && (
              <button
                onClick={onEmitir}
                className="whitespace-nowrap cursor-pointer flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-md border transition-colors text-white bg-teal-600 border-teal-600 hover:bg-teal-700"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-send-plane-line" /></span>
                Emitir {rondaLabel} ronda
              </button>
            )}
          </div>
        </div>

        {expanded && retro && (
          <div className="mt-3 pt-3 border-t border-teal-100">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Observaciones completas</p>
            <p className="text-xs text-slate-700 leading-relaxed">{retro.observaciones}</p>
          </div>
        )}
      </div>
    </div>
  );
}
